import React, { useEffect, useRef } from 'react';

import { ContentProviderRegister } from '../register';
import { DataContentProps } from '../types';
import { InsightCollectionDataType, InsightData } from './types';
import { useCorePageState } from '../../page/provider';
import { DataProviderElement, useDataState } from '../../data/provider';
import { ColumnSection, Component, Content, DataMode, PageDsl, SectionType } from '../../types';
import { DynamicTitleElement } from '../../common/dynamic.title/title';
import GenericWarnings from '../../design/warning/GenericWarnings';
import { ExportHideout } from '../../export/ExportHideout';
import { logger } from '@ecosystem/newron-client-lib';

const trimLeadingSlash = (str: string | undefined) => (str && str.startsWith('/') ? str.substring(1) : str);

const getComponentsFromDsl = (dsl: PageDsl, dataEndPoint: string) => {
  const section = dsl.sections.filter(s => s.type === SectionType.column) as ColumnSection[];

  /* 
    * To ensure proper compatibility, we replace the leading '/' before comparing it 
      to support collections that are already created with '/' in the endpoint.
  */
  const trimmedDataEndPoint = trimLeadingSlash(dataEndPoint);
  const components = section
    .map(s => s.columns[0]?.component)
    .filter(c => trimLeadingSlash(c.dataOptions?.providerInfo.endpoint) === trimmedDataEndPoint);
  return components as Component[];
};

const getComponentHeadingFromDsl = (dsl: PageDsl, dataEndPoint: string) => {
  const components = getComponentsFromDsl(dsl, dataEndPoint);
  return components[0]?.heading;
};

export const getContentsFromDsl = (dsl: PageDsl, contentType: string, dataEndPoint: string) => {
  const contents = getComponentsFromDsl(dsl, dataEndPoint).map(c => c.contents);
  return contents.flatMap(c => c.fixed.filter(f => f.type === contentType)) as Content[];
};

export const InsightElementWithData = (props: InsightData) => {
  const firstCall = useRef(true);
  const { dsl, globalWarnings, updatePageSettings, setIsDslLoadedForCollection, pageWarnings } = useCorePageState();
  const {
    content: { type: contentType, dataOptions },
    dataType,
    componentSettings,
    componentName,
    paramsForData,
    corePageSettings,
    proposition,
    collection: { onError, onLoaded, onExportReady },
  } = props;
  const dataEndPoint = dataOptions?.providerInfo?.endpoint;

  const isLive = dataType === InsightCollectionDataType.live;
  const heading = dsl && contentType ? getComponentHeadingFromDsl(dsl, dataEndPoint) : undefined;
  const contentsFromDsl = dsl && contentType ? getContentsFromDsl(dsl, contentType, dataEndPoint) : [];
  const isOldMoBrandBillboard =
    componentSettings?.moBillboardKpi?.length && !componentSettings?.moBillboardDataType?.length;

  const updatedComponentSettings = isOldMoBrandBillboard
    ? { ...componentSettings, moBillboardDataType: ['brand_name'] }
    : componentSettings;

  useEffect(() => {
    try {
      if (firstCall.current) return;

      setIsDslLoadedForCollection(false);
      updatePageSettings({ grouping: paramsForData?.grouping, timestamp: [`${new Date().getTime()}`] });
    } catch (e) {
      logger.error(e);
    } finally {
      firstCall.current = false;
    }
  }, [paramsForData.cell, paramsForData?.highlightedBrand]);

  if (!dsl || !contentsFromDsl.length) {
    return null;
  }

  return (
    <div>
      {heading?.title && (
        <DynamicTitleElement
          onChange={() => {}}
          settings={updatedComponentSettings}
          title={heading.title}
          inactive={true}
        />
      )}
      <ExportHideout>
        <div id={`${componentName}_export_content`} />
      </ExportHideout>
      {globalWarnings && <GenericWarnings warnings={globalWarnings} />}
      {pageWarnings && <GenericWarnings warnings={pageWarnings} />}
      {contentsFromDsl.map((content, idx) =>
        !content.dataOptions || content.dataOptions?.mode === DataMode.takeFromProvider ? (
          <InsightContentElement
            key={`${content.type}-${idx}`}
            {...props}
            content={content}
            collection={{ onLoaded, onExportReady }}
            componentHeadingTitle={heading?.title}
            proposition={proposition}
            componentSettings={updatedComponentSettings}
          />
        ) : (
          <DataProviderElement
            key={`${content.type}-${idx}`}
            dataOptions={content.dataOptions}
            paramsForData={paramsForData}
            urlRoot={corePageSettings.data.url}
            collection={{ isLive, onError }}
          >
            <InsightContentElement
              {...props}
              content={content}
              collection={{ onLoaded, onExportReady }}
              componentHeadingTitle={heading?.title}
              proposition={proposition}
              componentSettings={updatedComponentSettings}
            />
          </DataProviderElement>
        )
      )}
    </div>
  );
};

const InsightContentElement = ({ content, proposition, ...props }) => {
  const { type, instructions } = content;
  const { data: dataState, isDataLoadingFinished, isEnrichmentLoading } = useDataState();
  const Element = ContentProviderRegister.getInstance().getContent(type) as React.FC<DataContentProps>;

  if (!dataState || !isDataLoadingFinished) return null;

  return (
    <Element
      {...props}
      paramsForData={props.paramsForData}
      dataUrlRoot={props.corePageSettings.data.url}
      data={dataState}
      updateSettings={() => {}}
      dataStamp={1}
      isLoading={!isDataLoadingFinished}
      componentSettings={props.componentSettings}
      type={type}
      instructions={instructions}
      isEnrichmentLoading={isEnrichmentLoading}
      proposition={proposition}
    />
  );
};
